import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCatalogService, type ServiceComponentSummary } from '../../service-administration/services/catalogService';
import { useAwsAccountService } from '../../aws-account/services/awsAccountService';
import { useDeploymentList } from '../../service-administration/hooks/useDeploymentList';
import { useServiceComponentDependencies } from '../../service-administration/hooks/useServiceComponentDependencies';
import { rowConfigFor } from '../../service-administration/rowConfig';
import type { DependencyInfo } from '../../service-administration/hooks/useServiceComponentDependencies';
import { summarizeCell, type CellSummary } from '../landscapeStatus';
import type { AwsAccount, Deployment } from '../../../types/backend';

// Query keys for the landscape feature's server reads. Kept in one factory so
// the spoke-activity hook can share the same namespace. Accounts are keyed by
// customer; the service-component catalog is customer-independent.
export const landscapeKeys = {
  serviceComponents: () => ['landscape', 'serviceComponents'] as const,
  awsAccounts: (customerId: string | undefined) => ['landscape', 'awsAccounts', customerId] as const,
  spokeActivity: (customerId: string | undefined, deploymentIds: string) =>
    ['landscape', 'spokeActivity', customerId, deploymentIds] as const,
};

// Stable empty references so the memo below doesn't recompute on every render
// while a query is disabled or has no data yet.
const EMPTY_COMPONENTS: ServiceComponentSummary[] = [];
const EMPTY_ACCOUNTS: AwsAccount[] = [];

export interface LandscapeAccount {
  awsAccountId: string;
  name: string;
}

export interface LandscapeSummary {
  total: number;
  inSync: number;
  attention: number;
  reconciling: number;
  blocked: number;
  pendingSync: number;
}

export interface LandscapeMatrix {
  accounts: LandscapeAccount[];
  regions: string[];
  /** keyed `${awsAccountId}/${region}` — only present when the cell has deployments. */
  cells: Map<string, CellSummary>;
  /** deployments grouped by cell key — for the drill-in spoke view. */
  deploymentsByCell: Map<string, Deployment[]>;
  /** loaded service components — needed by DeploymentRow in the spoke view. */
  products: ServiceComponentSummary[];
  /** dependency info keyed by deployment id. */
  dependencies: Record<string, DependencyInfo[]>;
  summary: LandscapeSummary;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function cellKey(awsAccountId: string, region: string): string {
  return `${awsAccountId}/${region}`;
}

export function useLandscapeMatrix(customerId: string | undefined, isOpen: boolean): LandscapeMatrix {
  const catalogService = useCatalogService();
  const awsAccountService = useAwsAccountService();
  const { deployments, loading: depsLoading, error: depsError, refresh } = useDeploymentList(
    isOpen ? customerId : undefined,
  );

  // The catalog is best-effort: on error the products list is empty and the
  // matrix renders with code-derived row config only.
  const componentsQuery = useQuery({
    queryKey: landscapeKeys.serviceComponents(),
    queryFn: async () => (await catalogService.listServiceComponents()).products,
    enabled: isOpen,
  });

  const accountsQuery = useQuery({
    queryKey: landscapeKeys.awsAccounts(customerId),
    queryFn: async () => (await awsAccountService.getCustomerAwsAccounts(customerId as string)).customer_accounts,
    enabled: isOpen && !!customerId,
  });

  const products = isOpen ? (componentsQuery.data ?? EMPTY_COMPONENTS) : EMPTY_COMPONENTS;
  const awsAccounts = isOpen ? (accountsQuery.data ?? EMPTY_ACCOUNTS) : EMPTY_ACCOUNTS;
  const accountsError = isOpen && accountsQuery.error
    ? (accountsQuery.error instanceof Error ? accountsQuery.error.message : 'Failed to load AWS accounts')
    : null;

  const dependencies = useServiceComponentDependencies(deployments, products);

  return useMemo<LandscapeMatrix>(() => {
    const productByName = new Map(products.map((p) => [p.name, p]));
    const productById = new Map(products.map((p) => [p.id, p]));

    const accounts: LandscapeAccount[] = [...awsAccounts]
      .map((a) => ({ awsAccountId: a.aws_account_id, name: a.account_name || a.aws_account_id }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const regions = [...new Set(deployments.map((d) => d.region))].sort();

    const deploymentsByCell = new Map<string, Deployment[]>();
    deployments.forEach((d) => {
      const key = cellKey(d.account_id, d.region);
      const arr = deploymentsByCell.get(key);
      if (arr) arr.push(d);
      else deploymentsByCell.set(key, [d]);
    });

    const cells = new Map<string, CellSummary>();
    deploymentsByCell.forEach((cellDeployments, key) => {
      const rows = cellDeployments.map((d) =>
        rowConfigFor(d, productByName.get(d.product_name ?? '') ?? productById.get(d.product_id), dependencies[d.id]),
      );
      const [accountId, region] = key.split('/');
      cells.set(key, summarizeCell(accountId, region, cellDeployments, rows));
    });

    const summary: LandscapeSummary = { total: 0, inSync: 0, attention: 0, reconciling: 0, blocked: 0, pendingSync: 0 };
    cells.forEach((c) => {
      summary.total += c.total;
      summary.inSync += c.inSync;
      summary.attention += c.attention;
      summary.reconciling += c.reconciling;
      summary.blocked += c.blocked;
      summary.pendingSync += c.pendingSync;
    });

    return {
      accounts,
      regions,
      cells,
      deploymentsByCell,
      products,
      dependencies,
      summary,
      loading: depsLoading,
      error: depsError ?? accountsError,
      refresh,
    };
  }, [products, awsAccounts, deployments, dependencies, depsLoading, depsError, accountsError, refresh]);
}
